import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, X, AlertTriangle, RefreshCw, Loader2,
  Camera, Upload, LogIn, LogOutIcon
} from 'lucide-react';
import AppFooter from '../components/AppFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";

export default function OCRReviewPage() {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [editPlate, setEditPlate] = useState({});
  const [localPhotos, setLocalPhotos] = useState([]);
  const [isDetecting, setIsDetecting] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${API}/ocr/pending-review`, { withCredentials: true });
      setEvents(data);
    } catch {} finally { setIsLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const id = `local_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        setLocalPhotos(prev => [...prev, {
          id, image: ev.target.result, plate: '', confidence: 0, status: 'pending_detect'
        }]);
      };
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const detectFromPhoto = async (photo) => {
    setIsDetecting(photo.id);
    try {
      const { data } = await axios.post(`${API}/detect/ocr`, { image: photo.image, min_confidence: 0.15 }, { withCredentials: true, timeout: 60000 });
      if (data.plates?.length > 0) {
        const best = data.plates[0];
        setLocalPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, plate: best.plate, confidence: best.confidence, status: 'detected' } : p));
        setEditPlate(prev => ({ ...prev, [photo.id]: best.plate }));
        toast.success(`Detectada: ${best.plate} (${Math.round(best.confidence * 100)}%)`);
      } else {
        setLocalPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, status: 'no_plate' } : p));
        toast.error('No se detecto patente');
      }
    } catch { toast.error('Error al procesar'); }
    finally { setIsDetecting(null); }
  };

  const registerAction = async (photoOrEventId, action, plateOverride) => {
    const plate = plateOverride || editPlate[photoOrEventId];
    if (!plate || plate.length < 4) { toast.error('Patente muy corta'); return; }
    setProcessing(photoOrEventId);
    try {
      if (action === 'entry') {
        await axios.post(`${API}/vehicles/entry`, { plate, entry_type: 'ocr_review', vehicle_type: 'auto' }, { withCredentials: true });
        toast.success(`ENTRADA registrada: ${plate}`);
      } else {
        await axios.post(`${API}/vehicles/exit`, { plate }, { withCredentials: true });
        toast.success(`SALIDA registrada: ${plate}`);
      }
      // Remove from local photos or mark server event as accepted
      if (photoOrEventId.startsWith('local_')) {
        setLocalPhotos(prev => prev.filter(p => p.id !== photoOrEventId));
      } else {
        // Mark as accepted on server
        try { await axios.post(`${API}/ocr/review`, { event_id: photoOrEventId, action: 'accept', corrected_plate: plate }, { withCredentials: true }); } catch {}
        setEvents(prev => prev.filter(e => e.id !== photoOrEventId));
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(null); }
  };

  const removePhoto = (id) => setLocalPhotos(prev => prev.filter(p => p.id !== id));

  const handleReview = async (eventId, action, correctedPlate) => {
    setProcessing(eventId);
    try {
      await axios.post(`${API}/ocr/review`, { event_id: eventId, action, corrected_plate: correctedPlate }, { withCredentials: true });
      toast.success(action === 'accept' ? 'Aceptada' : 'Rechazada');
      setEvents(events.filter(e => e.id !== eventId));
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(null); }
  };

  const getConfColor = (c) => c >= 0.7 ? 'text-emerald-400 border-emerald-500' : c >= 0.4 ? 'text-amber-400 border-amber-500' : 'text-red-400 border-red-500';

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/dashboard"><Button variant="ghost" size="sm" className="text-slate-300 hover:text-white"><ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Volver</span></Button></Link>
            <div className="border-l border-slate-700 pl-2 sm:pl-4">
              <h1 className="text-base sm:text-lg font-bold text-white">Revision OCR</h1>
              <p className="text-xs text-slate-400 hidden sm:block">Fotos manuales y lecturas pendientes</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-amber-600">{events.length + localPhotos.length}</Badge>
            <input type="file" ref={fileInputRef} accept="image/*" onChange={handleFileUpload} className="hidden" />
            <Button size="sm" onClick={() => { fileInputRef.current.setAttribute('capture', 'environment'); fileInputRef.current.removeAttribute('multiple'); fileInputRef.current.click(); }} className="bg-blue-600 hover:bg-blue-700" data-testid="capture-photo-btn">
              <Camera className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Camara</span>
            </Button>
            <Button size="sm" variant="outline" onClick={() => { fileInputRef.current.removeAttribute('capture'); fileInputRef.current.setAttribute('multiple', ''); fileInputRef.current.click(); }} className="border-slate-700 text-slate-300" data-testid="upload-photo-btn">
              <Upload className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Galeria</span>
            </Button>
            <Button size="sm" variant="outline" onClick={fetchEvents} className="border-slate-700 text-slate-300" data-testid="refresh-ocr"><RefreshCw className="w-4 h-4" /></Button>
            <img src={LOGO_URL} alt="ParkingVision AI" className="h-6 sm:h-8 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 flex-1">
        {/* Local photos from camera/upload */}
        {localPhotos.length > 0 && (
          <div className="mb-6">
            <h2 className="text-white font-bold text-base mb-3 flex items-center gap-2"><Camera className="w-4 h-4 text-blue-400" />Fotos subidas ({localPhotos.length})</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {localPhotos.map((photo) => (
                <Card key={photo.id} className="bg-slate-800 border-slate-700" data-testid={`photo-${photo.id}`}>
                  <CardContent className="p-3">
                    <div className="rounded-lg overflow-hidden bg-slate-700 mb-3 h-32 sm:h-40 flex items-center justify-center">
                      <img src={photo.image} alt="Foto" className="max-w-full max-h-full object-contain" />
                    </div>
                    {photo.status === 'pending_detect' && (
                      <Button onClick={() => detectFromPhoto(photo)} disabled={isDetecting === photo.id} className="w-full bg-amber-600 hover:bg-amber-700 mb-2" data-testid={`detect-${photo.id}`}>
                        {isDetecting === photo.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
                        Detectar Patente
                      </Button>
                    )}
                    {photo.status === 'no_plate' && (
                      <div className="space-y-2">
                        <p className="text-red-400 text-xs text-center">No se detecto. Ingresa manualmente:</p>
                        <Input value={editPlate[photo.id] || ''} onChange={(e) => setEditPlate({...editPlate, [photo.id]: e.target.value.toUpperCase()})}
                          className="bg-slate-700 border-slate-600 text-white font-mono uppercase text-center" placeholder="ABC123" />
                      </div>
                    )}
                    {(photo.status === 'detected' || (photo.status === 'no_plate' && editPlate[photo.id]?.length >= 4)) && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Input value={editPlate[photo.id] || ''} onChange={(e) => setEditPlate({...editPlate, [photo.id]: e.target.value.toUpperCase()})}
                            className="bg-slate-700 border-slate-600 text-white font-mono uppercase text-center flex-1" />
                          {photo.confidence > 0 && <Badge variant="outline" className={getConfColor(photo.confidence)}>{Math.round(photo.confidence * 100)}%</Badge>}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Button size="sm" onClick={() => registerAction(photo.id, 'entry')} disabled={processing === photo.id} className="bg-emerald-600 hover:bg-emerald-700">
                            <LogIn className="w-3 h-3 mr-1" />Entrada
                          </Button>
                          <Button size="sm" onClick={() => registerAction(photo.id, 'exit')} disabled={processing === photo.id} className="bg-red-600 hover:bg-red-700">
                            <LogOutIcon className="w-3 h-3 mr-1" />Finalizar
                          </Button>
                        </div>
                      </div>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => removePhoto(photo.id)} className="w-full text-slate-500 mt-2 text-xs">Descartar</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Server-side pending OCR events */}
        {isLoading ? (
          <div className="text-center py-12 text-slate-400">Cargando...</div>
        ) : events.length === 0 && localPhotos.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <Check className="w-16 h-16 mx-auto mb-4 opacity-40 text-emerald-400" />
            <p className="text-lg text-emerald-400">Todo revisado</p>
            <p className="text-sm mt-1">Subi fotos con el boton de arriba para detectar patentes</p>
          </div>
        ) : events.length > 0 && (
          <div>
            <h2 className="text-white font-bold text-base mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" />Lecturas pendientes ({events.length})</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {events.map((evt) => (
                <Card key={evt.id} className="bg-slate-800 border-slate-700" data-testid={`ocr-event-${evt.id}`}>
                  <CardContent className="p-3">
                    {evt.crop_b64 ? (
                      <div className="rounded-lg overflow-hidden bg-slate-700 mb-3 h-24 flex items-center justify-center">
                        <img src={evt.crop_b64.startsWith('data:') ? evt.crop_b64 : `data:image/jpeg;base64,${evt.crop_b64}`} alt="Foto" className="max-w-full max-h-full object-contain" />
                      </div>
                    ) : (
                      <div className="rounded-lg bg-slate-700 mb-3 h-20 flex items-center justify-center">
                        <span className="text-slate-500 text-xs">Sin imagen</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <Input value={editPlate[evt.id] !== undefined ? editPlate[evt.id] : (evt.normalized_plate || '')}
                        onChange={(e) => setEditPlate({...editPlate, [evt.id]: e.target.value.toUpperCase()})}
                        className="bg-slate-700 border-slate-600 text-white font-mono uppercase text-center flex-1 h-9" placeholder="PATENTE" />
                      {evt.confidence > 0 && <Badge variant="outline" className={getConfColor(evt.confidence)}>{Math.round(evt.confidence * 100)}%</Badge>}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <Button size="sm" onClick={() => registerAction(evt.id, 'entry', editPlate[evt.id] || evt.normalized_plate)} disabled={processing === evt.id} className="bg-emerald-600 hover:bg-emerald-700">
                        <LogIn className="w-3 h-3 mr-1" />Entrada
                      </Button>
                      <Button size="sm" onClick={() => registerAction(evt.id, 'exit', editPlate[evt.id] || evt.normalized_plate)} disabled={processing === evt.id} className="bg-red-600 hover:bg-red-700">
                        <LogOutIcon className="w-3 h-3 mr-1" />Finalizar
                      </Button>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleReview(evt.id, 'reject')} disabled={processing === evt.id} className="w-full text-slate-500 text-xs">
                      <X className="w-3 h-3 mr-1" />Descartar
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      <AppFooter />
    </div>
  );
}
